import React from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import MaintenanceCard, { MaintenanceTool } from '../components/maintenance/MaintenanceCard';
import {
  getTokenExchangeStatus,
  repairTokenExchange,
  TokenExchangeStatus,
  TokenExchangeRepair,
  getAdminEmailStatus,
  syncAdminEmails,
  AdminEmailStatus,
  AdminEmailRepair,
  getSchemaStatus,
  SchemaStatus,
  getRedirectUriStatus,
  backfillRedirectUris,
  RedirectUriStatus,
  RedirectUriRepair,
  getStorageStatus,
  StorageStatus,
  getOrgConsistency,
  OrgConsistency,
} from '../api/maintenance';

// ── Tool configs ──────────────────────────────────────────────────────────────

const impersonationTool: MaintenanceTool<TokenExchangeStatus, TokenExchangeRepair> = {
  key: 'token-exchange',
  title: 'Impersonation Access',
  description: 'Checks whether each tenant has the Zitadel token-exchange grant needed for super-admin impersonation.',
  kind: 'repair',
  scan: getTokenExchangeStatus,
  stats: (d) => [
    { label: 'Total', value: d.total, tone: 'mute' },
    { label: 'Ready', value: d.present_count, tone: 'ok' },
    { label: 'Need fix', value: d.missing_count, tone: 'warn' },
  ],
  columns: [
    { key: 'company_name', label: 'Company' },
    { key: 'company_code', label: 'Code' },
  ],
  items: (d) => d.missing as Record<string, unknown>[],
  repair: {
    label: (d) => `Repair ${d.missing_count}`,
    enabled: (d) => d.missing_count > 0,
    run: repairTokenExchange,
    resultSummary: (r) =>
      `${r.fixed_count} fixed · ${r.already_count} already had it · ${r.failed_count} failed`,
  },
};

const adminEmailTool: MaintenanceTool<AdminEmailStatus, AdminEmailRepair> = {
  key: 'admin-email',
  title: 'Admin Email Sync',
  description: 'Detects tenant admin emails that have drifted from their Zitadel identity. Fixable entries can be synced automatically.',
  kind: 'repair',
  scan: getAdminEmailStatus,
  stats: (d) => [
    { label: 'Total', value: d.total, tone: 'mute' },
    { label: 'Match', value: d.ok_count, tone: 'ok' },
    { label: 'Stale', value: d.fixable_count, tone: 'warn' },
    { label: 'Needs review', value: d.ambiguous_count, tone: 'mute' },
  ],
  columns: [
    { key: 'company_code', label: 'Code' },
    { key: 'admin_name', label: 'Admin' },
    { key: 'old_email', label: 'Current' },
    { key: 'new_email', label: 'Zitadel' },
  ],
  items: (d) => d.fixable as Record<string, unknown>[],
  repair: {
    label: (d) => `Sync ${d.fixable_count}`,
    enabled: (d) => d.fixable_count > 0,
    run: syncAdminEmails,
    resultSummary: (r) =>
      `${r.fixed_count} fixed · ${r.skipped_count} skipped · ${r.failed_count} failed`,
  },
};

const schemaTool: MaintenanceTool<SchemaStatus> = {
  key: 'schema-status',
  title: 'Tenant Schema Verification',
  description: 'Compares each tenant database schema against the master template. Drifted tenants may be missing tables or columns.',
  kind: 'report',
  scan: getSchemaStatus,
  stats: (d) => [
    { label: 'Total', value: d.total, tone: 'mute' },
    { label: 'In sync', value: d.in_sync_count, tone: 'ok' },
    { label: 'Drifted', value: d.drifted_count, tone: 'warn' },
  ],
  columns: [
    { key: 'company_code', label: 'Code' },
    { key: 'missing_table_count', label: 'Missing tables' },
    { key: 'missing_column_count', label: 'Missing cols' },
  ],
  items: (d) => d.drifted as Record<string, unknown>[],
};

const redirectUriTool: MaintenanceTool<RedirectUriStatus, RedirectUriRepair> = {
  key: 'redirect-uris',
  title: 'Login Redirect URIs',
  description: 'Checks that each tenant application in Zitadel has all required redirect URIs registered for SSO login to work correctly.',
  kind: 'repair',
  scan: getRedirectUriStatus,
  stats: (d) => [
    { label: 'Total', value: d.total, tone: 'mute' },
    { label: 'OK', value: d.ok_count, tone: 'ok' },
    { label: 'Missing', value: d.missing_count, tone: 'warn' },
  ],
  columns: [
    { key: 'company_code', label: 'Code' },
    { key: 'missing', label: 'Missing URIs' },
  ],
  items: (d) => d.missing as Record<string, unknown>[],
  repair: {
    label: (d) => `Repair ${d.missing_count}`,
    enabled: (d) => d.missing_count > 0,
    run: backfillRedirectUris,
    resultSummary: (r) =>
      `${r.updated_count} updated · ${r.unchanged_count} unchanged · ${r.failed_count} failed`,
  },
};

const storageTool: MaintenanceTool<StorageStatus> = {
  key: 'storage-health',
  title: 'Photo Storage Health',
  description: 'Reports which tenants have photo storage configured (S3/RustFS/SharePoint) and which are missing a configuration.',
  kind: 'report',
  scan: getStorageStatus,
  stats: (d) => [
    { label: 'Total', value: d.total, tone: 'mute' },
    { label: 'Configured', value: d.configured_count, tone: 'ok' },
    { label: 'Unset', value: d.unconfigured_count, tone: 'warn' },
  ],
  columns: [
    { key: 'company_code', label: 'Code' },
    { key: 'company_name', label: 'Company' },
  ],
  items: (d) => d.unconfigured as Record<string, unknown>[],
};

const orgConsistencyTool: MaintenanceTool<OrgConsistency> = {
  key: 'org-consistency',
  title: 'Zitadel Org Consistency',
  description: 'Verifies that each tenant has a valid Zitadel organization with at least one admin user provisioned inside it.',
  kind: 'report',
  scan: getOrgConsistency,
  stats: (d) => [
    { label: 'Total', value: d.total, tone: 'mute' },
    { label: 'Consistent', value: d.consistent_count, tone: 'ok' },
    { label: 'Issues', value: d.org_not_found_count + d.no_admin_in_org_count, tone: 'warn' },
  ],
  columns: [
    { key: 'company_code', label: 'Code' },
    { key: 'reason', label: 'Reason' },
    { key: 'org_id', label: 'Org ID' },
  ],
  items: (d) => d.issues as Record<string, unknown>[],
};

// ── Page ──────────────────────────────────────────────────────────────────────

const tools = [
  impersonationTool,
  adminEmailTool,
  schemaTool,
  redirectUriTool,
  storageTool,
  orgConsistencyTool,
] as MaintenanceTool<unknown, unknown>[];

const Maintenance: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Page header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <WrenchScrewdriverIcon className="w-7 h-7 text-blue-600" />
            Maintenance &amp; Repairs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Scan for operational drift across tenants, then repair or review. Scans are read-only;
            repairs only add/append and are safe to re-run.
          </p>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {tools.map((tool) => (
            <MaintenanceCard key={tool.key} tool={tool} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
